import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { FormSelectInput } from "form";
import { actionFormValue } from "components/action-modal/utils/inputName";
import { setFromArrayOfObjects } from "utils/filterObject";

const CompositionSelectInput = ({
  substances,
  selectedPesticideId,
  isProposed,
  isViewMode,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const options = useCompositionOptions({
    substances,
    pesticideId: selectedPesticideId,
    isViewMode,
    isProposed,
  });

  const handleCompositionChange = (e) => {
    const { composition, compositionUnits } = JSON.parse(e.target.value);

    setFieldValue("drugAction.composition", composition).then(() => {
      setFieldTouched("drugAction.composition", true);
    });
    setFieldValue("drugAction.compositionUnits", compositionUnits).then(() => {
      setFieldTouched("drugAction.compositionUnits", true);
    });
    setFieldValue("drugAction.brandName", "").then(() => {
      setFieldTouched("drugAction.brandName", true);
    });
  };

  return (
    <FormSelectInput
      name="drugAction.composition"
      value={JSON.stringify({
        composition:
          actionFormValue(values, "drugAction", "composition", isProposed) + "",
        compositionUnits: actionFormValue(
          values,
          "drugAction",
          "compositionUnits",
          isProposed,
        ),
      })}
      placeholder={t("ActionModal.Composition")}
      label={isViewMode ? t("ActionModal.Composition") : undefined}
      shouldHaveErrorBehaviorAfterSubmit
      isDisabled={!values?.drugAction?.pesticideId}
      isViewMode={isViewMode}
      onChange={handleCompositionChange}
      options={options}
    />
  );
};

export default CompositionSelectInput;

const useCompositionOptions = ({
  substances,
  pesticideId,
  isViewMode,
  isProposed,
}) => {
  const { t } = useTranslation();
  const { values } = useFormikContext();

  const selectedSubstance = useMemo(
    () => substances?.[pesticideId],
    [substances, pesticideId],
  );

  const compositionOptions = useMemo(() => {
    if (isViewMode && isProposed) {
      return [
        {
          value: JSON.stringify({
            composition: values?.proposedAction?.composition + "",
            compositionUnits: values?.proposedAction?.compositionUnits,
          }),
          label:
            values?.proposedAction?.composition +
            " " +
            t(`OperationUnits.${values?.proposedAction?.compositionUnits}`),
          group: "notRecommended",
        },
      ];
    }

    const options = setFromArrayOfObjects(
      selectedSubstance?.value.map(({ agrochemicalRead }) => agrochemicalRead),
      "composition",
    )?.map(({ composition, compositionUnits }) => {
      return {
        label: composition + " " + t(`OperationUnits.${compositionUnits}`),
        value: JSON.stringify({
          composition: composition + "",
          compositionUnits,
        }),
      };
    });

    return options || [];
  }, [selectedSubstance, t, isViewMode, isProposed, values?.proposedAction]);

  return compositionOptions;
};
